/**
 * Demo Wallet Deposit Modal
 *
 * 모의거래 지갑 입금 모달
 * - 입금 금액 입력
 * - 최대 한도 안내 (1,000,000 USDT)
 * - 입금 후 잔액 자동 업데이트
 *
 * @see coin-server/apps/api/src/demo/handlers/demo-wallet-deposit.handler.ts
 */

import React, { useState } from 'react';
import { Modal, message } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getBigNumber } from '@utils/getBigNumber';
import { useDemoTradingStore } from '@app/hooks/trade/demoTradingStore';
import { demoWalletApi } from '@app/api/demoWallet';
import { useDemoWallet } from '@app/hooks/wallet/useUnifiedWallet';
import { useIsMobile } from '@/utils/CalVW';
import CBottomSheet from '@/app/components/Common/CBottomSheet';

// Icons
const closeIconUrl = '/assets/newHome/copy-trading/close-icon-transfer.svg';
const closeIconPcUrl = '/assets/newHome/copy-trading/close-icon-transfer-pc.svg';
const dividerIconUrl = '/assets/newHome/copy-trading/divider-line-icon.svg';

interface DemoWalletDepositModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export const DemoWalletDepositModal: React.FC<DemoWalletDepositModalProps> = ({
  open,
  setOpen,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const { account, updateBalance } = useDemoTradingStore();
  const { data: demoWallet, mutate: refreshUnifiedWallet } = useDemoWallet();

  const [depositAmount, setDepositAmount] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // 최대 한도 (환경변수에서 설정 가능, 기본 1,000,000 USDT)
  const MAX_BALANCE = 1000000;

  const handleCancel = () => {
    setOpen(false);
    setDepositAmount('');
  };

  const handleDeposit = async () => {
    const numAmount = parseFloat(depositAmount);

    if (!numAmount || numAmount <= 0) {
      message.error(t('demo.depositAmountRequired') || '입금 금액을 입력하세요');
      return;
    }

    if (numAmount < 1) {
      message.error(t('demo.minimumDepositAmount') || '최소 입금 금액은 1 USDT입니다');
      return;
    }

    const currentBalance = getBigNumber(account.balance);
    const newBalance = currentBalance.plus(numAmount);

    // 최대 한도 체크
    if (newBalance.isGreaterThan(MAX_BALANCE)) {
      const maxDeposit = getBigNumber(MAX_BALANCE).minus(currentBalance);
      message.error(
        t('demo.maxBalanceExceeded', {
          maxBalance: MAX_BALANCE.toLocaleString(),
          maxDeposit: maxDeposit.toFixed(2),
        }) ||
          `최대 잔액 ${MAX_BALANCE.toLocaleString()} USDT를 초과할 수 없습니다.`
      );
      return;
    }

    setIsLoading(true);
    try {
      const result = await demoWalletApi.depositToFutures(numAmount);
      updateBalance(result.data.balance);
      await refreshUnifiedWallet(undefined, { revalidate: true });

      message.success(
        t('demo.depositSuccess', { amount: numAmount }) ||
          `${numAmount.toLocaleString()} USDT가 입금되었습니다`
      );

      setDepositAmount('');

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        setOpen(false);
      }, 1000);
    } catch (error: any) {
      console.error('Demo wallet deposit error:', error);
      message.error(error.message || t('demo.depositFailed') || '입금에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePresetAmount = (amount: number) => {
    setDepositAmount(amount.toString());
  };

  const currentBalance = getBigNumber(demoWallet?.balance || account.balance || '0').toNumber();
  const maxDepositAllowed = getBigNumber(MAX_BALANCE).minus(currentBalance).toNumber();

  const handleMaxClick = () => {
    if (maxDepositAllowed > 0) {
      setDepositAmount(maxDepositAllowed.toFixed(0));
    }
  };

  const closeIcon = isMobile ? closeIconUrl : closeIconPcUrl;

  const content = (
    <>
      <Header>
        <Title>{t('demo.walletDeposit') || '모의거래 입금'}</Title>
        <CloseButton onClick={handleCancel}>
          <img src={closeIcon} alt="Close" style={{ width: '20px', height: '20px' }} />
        </CloseButton>
      </Header>

      <FormSection>
        {/* 현재 잔액 */}
        <BalanceBox>
          <BalanceLabel>{t('demo.currentBalance') || '현재 잔액'}</BalanceLabel>
          <BalanceValue>
            {currentBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
          </BalanceValue>
        </BalanceBox>

        {/* 입금 금액 입력 */}
        <FormGroup>
          <Label>{t('demo.depositAmount') || '입금 금액'}</Label>
          <AmountInput>
            <AmountInputField
              type="number"
              placeholder="0"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              min="1"
              max={maxDepositAllowed}
              disabled={isLoading}
            />
            <AmountRight>
              <CurrencyLabel>USDT</CurrencyLabel>
              <Divider>
                <img src={dividerIconUrl} alt="" style={{ width: '1px', height: '12px' }} />
              </Divider>
              <AllButton onClick={handleMaxClick} disabled={isLoading}>
                MAX
              </AllButton>
            </AmountRight>
          </AmountInput>
        </FormGroup>

        {/* 프리셋 금액 버튼 */}
        <PresetSection>
          <PresetButton
            onClick={() => handlePresetAmount(100)}
            disabled={isLoading || maxDepositAllowed < 100}
          >
            +100
          </PresetButton>
          <PresetButton
            onClick={() => handlePresetAmount(1000)}
            disabled={isLoading || maxDepositAllowed < 1000}
          >
            +1,000
          </PresetButton>
          <PresetButton
            onClick={() => handlePresetAmount(10000)}
            disabled={isLoading || maxDepositAllowed < 10000}
          >
            +10,000
          </PresetButton>
          <PresetButton
            onClick={() => handlePresetAmount(100000)}
            disabled={isLoading || maxDepositAllowed < 100000}
          >
            +100,000
          </PresetButton>
        </PresetSection>

        {/* 입금 후 예상 잔액 */}
        <SummaryBox>
          <SummaryLabel>{t('demo.afterDeposit') || '입금 후 잔액'}</SummaryLabel>
          <SummaryValue>
            {getBigNumber(currentBalance)
              .plus(parseFloat(depositAmount) || 0)
              .toNumber()
              .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
            USDT
          </SummaryValue>
        </SummaryBox>

        {/* 안내 메시지 */}
        <NoticeBox>
          <NoticeItem>• {t('demo.noticeMinAmount') || '최소 입금: 1 USDT'}</NoticeItem>
          <NoticeItem>• {t('demo.noticeMaxBalance') || `최대 잔액: ${MAX_BALANCE.toLocaleString()} USDT`}</NoticeItem>
          <NoticeItem>• {t('demo.noticeDemoMode') || '모의거래 전용 - 실제 자금 이동 없음'}</NoticeItem>
        </NoticeBox>
      </FormSection>

      <ConfirmButton onClick={handleDeposit} disabled={isLoading}>
        {isLoading ? t('processing') || '처리중...' : t('demo.deposit') || '입금'}
      </ConfirmButton>
    </>
  );

  if (isMobile) {
    return (
      <CBottomSheet
        isOpen={open}
        onClose={handleCancel}
        height="auto"
        sheetContainerStyle={{ background: '#0c2323' }}
      >
        <MobileModalContent>{content}</MobileModalContent>
      </CBottomSheet>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closable={false}
      centered
      width={424}
      styles={{
        body: { padding: 0 },
        content: {
          background: '#0c2323',
          boxShadow: 'none',
          padding: 0,
          borderRadius: '16px',
        },
        mask: { background: 'rgba(0, 0, 0, 0.8)' },
      }}
    >
      <PCModalContent>{content}</PCModalContent>
    </Modal>
  );
};

// Styled Components
const MobileModalContent = styled.div`
  background: #0c2323;
  border-radius: 16px 16px 0 0;
  padding: 24px 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PCModalContent = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.36px;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const BalanceBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BalanceLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const BalanceValue = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #07d993;
  letter-spacing: -0.32px;
`;

const AmountInput = styled.div`
  border: 1px solid #9c27b0;
  border-radius: 12px;
  height: 50px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
`;

const AmountInputField = styled.input`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.32px;
  background: transparent;
  border: none;
  outline: none;
  flex: 1;
  width: 0;

  &::placeholder {
    color: #b3b5bd;
    font-weight: 400;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const AmountRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CurrencyLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AllButton = styled.button`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #9c27b0;
  letter-spacing: -0.28px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PresetSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const PresetButton = styled.button`
  padding: 12px 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  border-radius: 8px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(156, 39, 176, 0.2);
    border-color: #9c27b0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SummaryBox = styled.div`
  background: rgba(156, 39, 176, 0.1);
  border: 1px solid rgba(156, 39, 176, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SummaryLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const SummaryValue = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #9c27b0;
  letter-spacing: -0.32px;
`;

const NoticeBox = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const NoticeItem = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  letter-spacing: -0.24px;
  line-height: 1.5;
`;

const ConfirmButton = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(90deg, #7b1fa2 0%, #9c27b0 100%);
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.3px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
